import React, { useState, useEffect, useRef } from 'react';
import { Layout } from '../../components/Layout';
import { AgentSpec, AgentSpecStatus } from '../../types';
import { agentSpecsApi } from '../../services/agentRuns';
import { formatLocalDate } from '../../utils/date';

/**
 * Admin page for managing agent specifications.
 * SPEDflow Admin only - allows viewing, creating, and updating agent specs.
 * 
 * Requirements: 2.8.1.1, 2.8.2.1
 * - List all agent specs
 * - View spec details
 * - Create/update specs via JSON upload
 * - Change spec status
 */
export const AgentSpecAdmin: React.FC = () => {
  const [specs, setSpecs] = useState<AgentSpec[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedSpec, setSelectedSpec] = useState<AgentSpec | null>(null);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [statusFilter, setStatusFilter] = useState<AgentSpecStatus | 'all'>('all');

  const fetchSpecs = async () => {
    try {
      setLoading(true);
      setError(null);
      const status = statusFilter === 'all' ? undefined : statusFilter;
      const response = await agentSpecsApi.list(status);
      setSpecs(response.specs);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load agent specs');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSpecs();
  }, [statusFilter]);

  const getStatusBadge = (status: AgentSpecStatus) => {
    const statusConfig = {
      draft: { bg: '#e3f2fd', text: '#1565c0' },
      active: { bg: '#e8f5e9', text: '#2e7d32' },
      deprecated: { bg: '#f5f5f5', text: '#616161' },
    };
    const config = statusConfig[status];

    return (
      <span
        style={{
          backgroundColor: config.bg,
          color: config.text,
          padding: '4px 12px',
          borderRadius: '12px',
          fontSize: '12px',
          fontWeight: 500,
          textTransform: 'capitalize',
        }}
      >
        {status}
      </span>
    );
  };

  return (
    <Layout>
      <div style={{ padding: '24px' }}>
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
          <div>
            <h1 style={{ margin: '0 0 8px' }}>Agent Specifications</h1>
            <p style={{ margin: 0, color: '#666' }}>
              Manage AI agent definitions and configurations
            </p>
          </div>
          <button
            onClick={() => setShowUploadModal(true)}
            style={{
              padding: '10px 20px',
              border: 'none',
              borderRadius: '4px',
              backgroundColor: '#1976d2',
              color: 'white',
              cursor: 'pointer',
              fontWeight: 500,
            }}
            data-testid="upload-spec-button"
          >
            + Upload Spec
          </button>
        </div>

        {/* Filters */}
        <div style={{ marginBottom: '16px', display: 'flex', gap: '12px', alignItems: 'center' }}>
          <label style={{ fontWeight: 500 }}>Status:</label>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as AgentSpecStatus | 'all')}
            style={{
              padding: '8px 12px',
              border: '1px solid #ddd',
              borderRadius: '4px',
            }}
            data-testid="status-filter"
          >
            <option value="all">All</option>
            <option value="active">Active</option>
            <option value="draft">Draft</option>
            <option value="deprecated">Deprecated</option>
          </select>
        </div>

        {/* Error display */}
        {error && (
          <div
            style={{
              padding: '12px',
              backgroundColor: '#ffebee',
              color: '#c62828',
              borderRadius: '4px',
              marginBottom: '16px',
            }}
            data-testid="error-message"
          >
            {error}
          </div>
        )}

        {/* Loading state */}
        {loading ? (
          <div style={{ textAlign: 'center', padding: '48px', color: '#666' }}>
            Loading agent specs...
          </div>
        ) : specs.length === 0 ? (
          <div
            style={{
              textAlign: 'center',
              padding: '48px',
              backgroundColor: '#fafafa',
              borderRadius: '8px',
            }}
          >
            <div style={{ fontSize: '48px', marginBottom: '16px' }}>🤖</div>
            <div style={{ color: '#666' }}>No agent specs found</div>
            <button
              onClick={() => setShowUploadModal(true)}
              style={{
                marginTop: '16px',
                padding: '8px 16px',
                border: '1px solid #1976d2',
                borderRadius: '4px',
                backgroundColor: 'white',
                color: '#1976d2',
                cursor: 'pointer',
              }}
            >
              Upload your first spec
            </button>
          </div>
        ) : (
          <div style={{ display: 'flex', gap: '24px' }}>
            {/* Spec list */}
            <div style={{ flex: 1 }}>
              <div
                style={{
                  backgroundColor: 'white',
                  borderRadius: '8px',
                  border: '1px solid #e0e0e0',
                  overflow: 'hidden',
                }}
              >
                {specs.map((spec, index) => (
                  <div
                    key={`${spec.agentId}-${spec.version}`}
                    onClick={() => setSelectedSpec(spec)}
                    style={{
                      padding: '16px',
                      borderBottom: index < specs.length - 1 ? '1px solid #e0e0e0' : 'none',
                      cursor: 'pointer',
                      backgroundColor: selectedSpec?.agentId === spec.agentId && selectedSpec?.version === spec.version
                        ? '#f5f5f5'
                        : 'white',
                    }}
                    data-testid={`spec-${spec.agentId}`}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                      <div>
                        <div style={{ fontWeight: 600, marginBottom: '4px' }}>{spec.name}</div>
                        <div style={{ fontSize: '14px', color: '#666', marginBottom: '8px' }}>
                          {spec.agentId} v{spec.version}
                        </div>
                        {spec.description && (
                          <div style={{ fontSize: '14px', color: '#666' }}>
                            {spec.description.length > 100
                              ? `${spec.description.substring(0, 100)}...`
                              : spec.description}
                          </div>
                        )}
                      </div>
                      {getStatusBadge(spec.status)}
                    </div>
                    <div style={{ marginTop: '8px', fontSize: '12px', color: '#999' }}>
                      {spec.steps.length} steps • {spec.toolAllowlist.length} tools
                      {spec.createdAt && ` • Created ${formatLocalDate(spec.createdAt)}`}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Spec detail panel */}
            {selectedSpec && (
              <SpecDetailPanel
                spec={selectedSpec}
                onStatusChange={async (newStatus) => {
                  try {
                    await agentSpecsApi.updateStatus(selectedSpec.agentId, newStatus, selectedSpec.version);
                    await fetchSpecs();
                    setSelectedSpec({ ...selectedSpec, status: newStatus as AgentSpecStatus });
                  } catch (err) {
                    setError(err instanceof Error ? err.message : 'Failed to update status');
                  }
                }}
                onClose={() => setSelectedSpec(null)}
              />
            )}
          </div>
        )}

        {/* Upload Modal */}
        {showUploadModal && (
          <UploadSpecModal
            onClose={() => setShowUploadModal(false)}
            onUploaded={() => {
              setShowUploadModal(false);
              fetchSpecs();
            }}
          />
        )}
      </div>
    </Layout>
  );
};

interface SpecDetailPanelProps {
  spec: AgentSpec;
  onStatusChange: (status: string) => Promise<void>;
  onClose: () => void;
}

const SpecDetailPanel: React.FC<SpecDetailPanelProps> = ({ spec, onStatusChange, onClose }) => {
  const [changingStatus, setChangingStatus] = useState(false);
  const [showJson, setShowJson] = useState(false);

  const handleStatusChange = async (newStatus: string) => {
    try {
      setChangingStatus(true);
      await onStatusChange(newStatus);
    } finally {
      setChangingStatus(false);
    }
  };

  return (
    <div
      style={{
        width: '400px',
        backgroundColor: 'white',
        borderRadius: '8px',
        border: '1px solid #e0e0e0',
        overflow: 'hidden',
      }}
      data-testid="spec-detail-panel"
    >
      {/* Header */}
      <div
        style={{
          padding: '16px',
          borderBottom: '1px solid #e0e0e0',
          backgroundColor: '#fafafa',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <h3 style={{ margin: 0 }}>{spec.name}</h3>
        <button
          onClick={onClose}
          style={{
            background: 'none',
            border: 'none',
            fontSize: '18px',
            cursor: 'pointer',
            color: '#666',
          }}
        >
          ×
        </button>
      </div>

      {/* Content */}
      <div style={{ padding: '16px', maxHeight: '600px', overflow: 'auto' }}>
        {/* Basic info */}
        <div style={{ marginBottom: '16px' }}>
          <div style={{ fontSize: '12px', color: '#666', marginBottom: '4px' }}>Agent ID</div>
          <div style={{ fontFamily: 'monospace' }}>{spec.agentId}</div>
        </div>

        <div style={{ marginBottom: '16px' }}>
          <div style={{ fontSize: '12px', color: '#666', marginBottom: '4px' }}>Version</div>
          <div>{spec.version}</div>
        </div>

        <div style={{ marginBottom: '16px' }}>
          <div style={{ fontSize: '12px', color: '#666', marginBottom: '4px' }}>Status</div>
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            <select
              value={spec.status}
              onChange={(e) => handleStatusChange(e.target.value)}
              disabled={changingStatus}
              style={{
                padding: '6px 10px',
                border: '1px solid #ddd',
                borderRadius: '4px',
              }}
              data-testid="status-select"
            >
              <option value="draft">Draft</option>
              <option value="active">Active</option>
              <option value="deprecated">Deprecated</option>
            </select>
            {changingStatus && <span style={{ fontSize: '12px', color: '#666' }}>Updating...</span>}
          </div>
        </div>

        {spec.description && (
          <div style={{ marginBottom: '16px' }}>
            <div style={{ fontSize: '12px', color: '#666', marginBottom: '4px' }}>Description</div>
            <div>{spec.description}</div>
          </div>
        )}

        {/* Triggers */}
        <div style={{ marginBottom: '16px' }}>
          <div style={{ fontSize: '12px', color: '#666', marginBottom: '4px' }}>Triggers</div>
          <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
            {spec.triggers.map((trigger, i) => (
              <span
                key={i}
                style={{
                  padding: '2px 8px',
                  backgroundColor: '#e3f2fd',
                  borderRadius: '4px',
                  fontSize: '12px',
                }}
              >
                {trigger.type}
              </span>
            ))}
          </div>
        </div>

        {/* Steps */}
        <div style={{ marginBottom: '16px' }}>
          <div style={{ fontSize: '12px', color: '#666', marginBottom: '8px' }}>
            Steps ({spec.steps.length})
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
            {spec.steps.map((step, i) => (
              <div
                key={step.stepId}
                style={{
                  padding: '8px',
                  backgroundColor: '#fafafa',
                  borderRadius: '4px',
                  fontSize: '14px',
                }}
              >
                <span style={{ fontWeight: 500 }}>{i + 1}. {step.name}</span>
                <span style={{ color: '#666', marginLeft: '8px' }}>({step.type})</span>
              </div>
            ))}
          </div>
        </div>

        {/* Tools */}
        <div style={{ marginBottom: '16px' }}>
          <div style={{ fontSize: '12px', color: '#666', marginBottom: '4px' }}>
            Allowed Tools ({spec.toolAllowlist.length})
          </div>
          <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
            {spec.toolAllowlist.map((tool) => (
              <span
                key={tool}
                style={{
                  padding: '2px 8px',
                  backgroundColor: '#f5f5f5',
                  borderRadius: '4px',
                  fontSize: '12px',
                  fontFamily: 'monospace',
                }}
              >
                {tool}
              </span>
            ))}
          </div>
        </div>

        {/* Guardrails */}
        <div style={{ marginBottom: '16px' }}>
          <div style={{ fontSize: '12px', color: '#666', marginBottom: '4px' }}>Guardrails</div>
          <div style={{ fontSize: '14px' }}>
            <div>
              ✓ Citations required: {spec.guardrails.requireCitationsForClaims ? 'Yes' : 'No'}
            </div>
            <div>
              ✓ No legal conclusions: {spec.guardrails.noLegalConclusions ? 'Yes' : 'No'}
            </div>
            <div>
              ✓ Min confidence: {spec.guardrails.humanReview.minConfidence}
            </div>
          </div>
        </div>

        {/* JSON view toggle */}
        <div>
          <button
            onClick={() => setShowJson(!showJson)}
            style={{
              background: 'none',
              border: 'none',
              color: '#1976d2',
              cursor: 'pointer',
              fontSize: '14px',
              padding: 0,
            }}
          >
            {showJson ? '▼ Hide JSON' : '▶ View JSON'}
          </button>
          {showJson && (
            <pre
              style={{
                marginTop: '8px',
                padding: '12px',
                backgroundColor: '#f5f5f5',
                borderRadius: '4px',
                fontSize: '12px',
                overflow: 'auto',
                maxHeight: '300px',
              }}
            >
              {JSON.stringify(spec, null, 2)}
            </pre>
          )}
        </div>
      </div>
    </div>
  );
};

interface UploadSpecModalProps {
  onClose: () => void;
  onUploaded: () => void;
}

const UploadSpecModal: React.FC<UploadSpecModalProps> = ({ onClose, onUploaded }) => {
  const [jsonContent, setJsonContent] = useState('');
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isUpdate, setIsUpdate] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (event) => {
      const content = event.target?.result as string;
      setJsonContent(content);
      try {
        JSON.parse(content);
        setError(null);
      } catch {
        setError('Invalid JSON format');
      }
    };
    reader.readAsText(file);
  };

  const handleUpload = async () => {
    try {
      setUploading(true);
      setError(null);

      const spec = JSON.parse(jsonContent);
      
      if (isUpdate && spec.agentId) {
        await agentSpecsApi.update(spec.agentId, spec);
      } else {
        await agentSpecsApi.create(spec);
      }
      
      onUploaded();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to upload spec');
    } finally {
      setUploading(false);
    }
  };

  const isValidJson = () => {
    try {
      JSON.parse(jsonContent);
      return true;
    } catch {
      return false;
    }
  };

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0,0,0,0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
      }}
      onClick={(e) => e.target === e.currentTarget && onClose()}
      data-testid="upload-spec-modal"
    >
      <div
        style={{
          backgroundColor: 'white',
          borderRadius: '8px',
          maxWidth: '600px',
          width: '90%',
          maxHeight: '90vh',
          overflow: 'auto',
        }}
      >
        {/* Header */}
        <div
          style={{
            padding: '16px',
            borderBottom: '1px solid #e0e0e0',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <h3 style={{ margin: 0 }}>Upload Agent Spec</h3>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              fontSize: '20px',
              cursor: 'pointer',
              color: '#666',
            }}
          >
            ×
          </button>
        </div>

        {/* Content */}
        <div style={{ padding: '16px' }}>
          {error && (
            <div
              style={{
                padding: '12px',
                backgroundColor: '#ffebee',
                color: '#c62828',
                borderRadius: '4px',
                marginBottom: '16px',
              }}
              data-testid="upload-error"
            >
              {error}
            </div>
          )}

          {/* Mode toggle */}
          <div style={{ marginBottom: '16px' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={isUpdate}
                onChange={(e) => setIsUpdate(e.target.checked)}
              />
              <span>Update existing spec (creates new version)</span>
            </label>
          </div>

          {/* File upload */}
          <div style={{ marginBottom: '16px' }}>
            <input
              ref={fileInputRef}
              type="file"
              accept=".json"
              onChange={handleFileSelect}
              style={{ display: 'none' }}
            />
            <button
              onClick={() => fileInputRef.current?.click()}
              style={{
                padding: '8px 16px',
                border: '1px solid #ddd',
                borderRadius: '4px',
                backgroundColor: 'white',
                cursor: 'pointer',
              }}
            >
              📁 Select JSON File
            </button>
          </div>

          {/* JSON editor */}
          <div style={{ marginBottom: '16px' }}>
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: 500 }}>
              Or paste JSON directly:
            </label>
            <textarea
              value={jsonContent}
              onChange={(e) => {
                setJsonContent(e.target.value);
                try {
                  JSON.parse(e.target.value);
                  setError(null);
                } catch {
                  if (e.target.value.trim()) {
                    setError('Invalid JSON format');
                  }
                }
              }}
              placeholder='{"agentId": "...", "name": "...", ...}'
              style={{
                width: '100%',
                minHeight: '200px',
                padding: '12px',
                border: '1px solid #ddd',
                borderRadius: '4px',
                fontFamily: 'monospace',
                fontSize: '12px',
                resize: 'vertical',
              }}
              data-testid="json-input"
            />
          </div>

          {/* Actions */}
          <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
            <button
              onClick={onClose}
              disabled={uploading}
              style={{
                padding: '8px 16px',
                border: '1px solid #ddd',
                borderRadius: '4px',
                backgroundColor: 'white',
                cursor: uploading ? 'not-allowed' : 'pointer',
              }}
            >
              Cancel
            </button>
            <button
              onClick={handleUpload}
              disabled={uploading || !jsonContent.trim() || !isValidJson()}
              style={{
                padding: '8px 16px',
                border: 'none',
                borderRadius: '4px',
                backgroundColor: '#1976d2',
                color: 'white',
                cursor: uploading || !jsonContent.trim() || !isValidJson() ? 'not-allowed' : 'pointer',
                opacity: uploading || !jsonContent.trim() || !isValidJson() ? 0.6 : 1,
              }}
              data-testid="upload-button"
            >
              {uploading ? 'Uploading...' : isUpdate ? 'Update Spec' : 'Create Spec'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AgentSpecAdmin;
